import { useState, useEffect, useCallback } from "react"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import styles from "./dashboard-page.module.css"

interface DashboardStats {
  totalUsers: number
  newUsersThisMonth: number
  activeTasks: number
  totalCourses: number
  totalRevenue: number
  completionRate: number
}

interface ActivityItem {
  id: string
  type: "user" | "task" | "course" | "hackathon" | "finance"
  text: string
  time: string
}

const ACTIVITY_ICONS: Record<ActivityItem["type"], string> = {
  user: "👤",
  task: "📋",
  course: "🎓",
  hackathon: "🏆",
  finance: "💰",
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return "刚刚"
  if (minutes < 60) return `${minutes}分钟前`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}小时前`
  const days = Math.floor(hours / 24)
  return `${days}天前`
}

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    newUsersThisMonth: 0,
    activeTasks: 0,
    totalCourses: 0,
    totalRevenue: 0,
    completionRate: 0,
  })
  const [activities, setActivities] = useState<ActivityItem[]>([])
  const [loading, setLoading] = useState(true)
  const [monthlyGrowth, setMonthlyGrowth] = useState<number[]>([])

  const fetchDashboard = useCallback(async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) { setLoading(false); return }

    setLoading(true)

    try {
      const now = new Date()
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()

      const [usersRes, tasksRes, coursesRes, bountiesRes] = await Promise.all([
        supabase.from("users").select("id, created_at", { count: "exact", head: false }),
        supabase.from("bounties").select("id, status", { count: "exact", head: false }),
        supabase.from("courses").select("id", { count: "exact", head: true }),
        supabase.from("escrow_records").select("platform_fee"),
      ])

      const allUsers = usersRes.data ?? []
      const totalUsers = usersRes.count ?? allUsers.length
      const newUsersThisMonth = allUsers.filter((u) => u.created_at >= monthStart).length

      const allTasks = tasksRes.data ?? []
      const activeTasks = allTasks.filter((t) => t.status === "bidding" || t.status === "in_progress" || t.status === "reviewing").length
      const completedTasks = allTasks.filter((t) => t.status === "completed" || t.status === "settled").length
      const totalTasks = allTasks.length
      const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0

      const totalCourses = coursesRes.count ?? 0

      const settlements = bountiesRes.data ?? []
      const totalRevenue = settlements.reduce((sum, s) => sum + (s.platform_fee ?? 0), 0)

      setStats({
        totalUsers,
        newUsersThisMonth,
        activeTasks,
        totalCourses,
        totalRevenue,
        completionRate,
      })

      // Monthly growth (last 10 months)
      const growth: number[] = []
      for (let i = 9; i >= 0; i--) {
        const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1)
        const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1)
        const count = allUsers.filter(
          (u) => u.created_at >= monthDate.toISOString() && u.created_at < nextMonth.toISOString()
        ).length
        growth.push(count)
      }
      setMonthlyGrowth(growth)

      // Recent activities from various tables
      const activityItems: ActivityItem[] = []

      const [recentUsers, recentBounties, recentCourses] = await Promise.all([
        supabase.from("users").select("id, nickname, created_at").order("created_at", { ascending: false }).limit(3),
        supabase.from("bounties").select("id, title, status, created_at").order("created_at", { ascending: false }).limit(3),
        supabase.from("courses").select("id, title, created_at").order("created_at", { ascending: false }).limit(2),
      ])

      for (const u of recentUsers.data ?? []) {
        activityItems.push({ id: u.id, type: "user", text: `新用户 ${u.nickname} 注册`, time: u.created_at })
      }
      for (const t of recentBounties.data ?? []) {
        activityItems.push({ id: t.id, type: "task", text: `任务「${t.title}」状态更新为 ${t.status}`, time: t.created_at })
      }
      for (const c of recentCourses.data ?? []) {
        activityItems.push({ id: c.id, type: "course", text: `课程「${c.title}」已上线`, time: c.created_at })
      }

      activityItems.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
      setActivities(activityItems.slice(0, 6))
    } catch (error) {
      console.error("Dashboard load failed:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchDashboard() }, [fetchDashboard])

  const maxGrowth = Math.max(...monthlyGrowth, 1)

  // Generate month labels for the chart
  const monthLabels: string[] = []
  for (let i = 9; i >= 0; i--) {
    const d = new Date()
    d.setMonth(d.getMonth() - i)
    monthLabels.push(d.toLocaleDateString('zh-CN', { month: 'short' }))
  }

  return (
    <div className={styles.page}>
      {/* Tech Theme Background Effects */}
      <div className={styles.techGridBg} />
      <div className={styles.techOrbs}>
        <div className={styles.techOrb} />
        <div className={styles.techOrb} />
        <div className={styles.techOrb} />
      </div>
      <div className={styles.techScanlines} />

      <div className={styles.header}>
        <h1 className={styles.title}>仪表盘</h1>
        <p className={styles.subtitle}>实时监控平台数据与业务动态</p>
      </div>

      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>👥</div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{loading ? "—" : stats.totalUsers.toLocaleString()}</span>
            <span className={styles.statLabel}>总用户</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>📋</div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{loading ? "—" : stats.activeTasks.toLocaleString()}</span>
            <span className={styles.statLabel}>进行中任务</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>💰</div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{loading ? "—" : `¥${stats.totalRevenue.toLocaleString()}`}</span>
            <span className={styles.statLabel}>累计结算</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>🎓</div>
          <div className={styles.statContent}>
            <span className={styles.statValue}>{loading ? "—" : stats.totalCourses}</span>
            <span className={styles.statLabel}>课程数</span>
          </div>
        </div>
      </div>

      <div className={styles.charts}>
        <div className={styles.chartCard}>
          <h3>用户注册趋势（近10月）</h3>
          <div className={styles.chartPlaceholder}>
            <div className={styles.barChart}>
              {monthlyGrowth.length > 0 ? monthlyGrowth.map((count, i) => (
                <div key={i} className={styles.barWrapper}>
                  <div
                    className={styles.bar}
                    style={{ height: `${Math.max((count / maxGrowth) * 100, 4)}%` }}
                    title={`${monthLabels[i]}: ${count} 人`}
                  />
                  <span className={styles.barLabel}>{monthLabels[i]}</span>
                </div>
              )) : (
                <div className={styles.emptyState}>暂无数据</div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.chartCard}>
          <h3>任务完成率</h3>
          <div className={styles.chartPlaceholder}>
            <div
              className={styles.donutChart}
              style={{ '--progress': `${stats.completionRate}%` } as React.CSSProperties}
            >
              <div className={styles.donutRing} />
              <div className={styles.donutInner}>
                <span className={styles.donutLabel}>{loading ? "—" : `${stats.completionRate}%`}</span>
                <span className={styles.donutSublabel}>完成率</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.recentActivity}>
        <h3>最近活动</h3>
        <div className={styles.activityList}>
          {activities.length === 0 ? (
            <div className={styles.emptyState}>
              {loading ? "加载中..." : "暂无活动记录"}
            </div>
          ) : (
            activities.map((item) => (
              <div key={item.id + item.type} className={styles.activityItem} data-type={item.type}>
                <span className={styles.activityIcon}>{ACTIVITY_ICONS[item.type]}</span>
                <span className={styles.activityText}>{item.text}</span>
                <span className={styles.activityTime}>{formatRelativeTime(item.time)}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
